import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { settingsAPI } from '@/services/api';

/**
 * Frontend S3 Upload Utility
 * Handles direct file uploads to S3 from the frontend using AWS SDK v3
 */

let s3Client = null;
let s3Config = null;

/**
 * Initialize S3 configuration by fetching from backend
 */
const initializeS3Config = async () => {
  try {
    
    const response = await settingsAPI.getCloudStorageSettings();
    
    if (!response.data.success) {
      throw new Error('Failed to fetch cloud storage settings');
    }

    const { data } = response.data;
    
    if (!data.access_key || !data.secret_key || !data.bucket) {
      throw new Error('AWS credentials not configured. Please contact administrator.');
    }

    s3Config = {
      accessKeyId: data.access_key,
      secretAccessKey: data.secret_key,
      region: data.region || 'us-east-1',
      bucket: data.bucket
    };

    // Initialize S3 client with v3 SDK
    s3Client = new S3Client({
      region: s3Config.region,
      credentials: {
        accessKeyId: s3Config.accessKeyId,
        secretAccessKey: s3Config.secretAccessKey,
      },
    });
    
    
    return s3Config;
  } catch (error) {
    console.error('❌ S3Upload: Failed to initialize AWS configuration:', error);
    throw error;
  }
};

/**
 * Upload a screenshot (base64) to S3 and return the URL
 * @param {string} base64Data - Base64 encoded image data
 * @param {string} companyId - Company ID for folder structure
 * @param {string} candidateId - Candidate ID for folder structure
 * @param {string} examId - Exam ID for folder structure
 * @returns {Promise<string>} - S3 URL of uploaded screenshot
 */
export const uploadScreenshotToS3 = async (base64Data, companyId, candidateId, examId) => {
  try {

    // Initialize S3 if not already done
    if (!s3Client || !s3Config) {
      await initializeS3Config();
    }

    // Create folder structure: companyId/candidateId/examId/
    const folderPath = `${companyId}/${candidateId}/${examId}`;
    const timestamp = Date.now();
    const fileName = `screenshot_${timestamp}.jpg`;
    const fileKey = `${folderPath}/${fileName}`;


    // Convert base64 to Uint8Array (browser-compatible)
    const base64Content = base64Data.replace(/^data:image\/\w+;base64,/, '');
    
    // Convert base64 to binary string, then to Uint8Array
    const binaryString = atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    

    const uploadCommand = new PutObjectCommand({
      Bucket: s3Config.bucket,
      Key: fileKey,
      Body: bytes,
      ContentType: 'image/jpeg',
      ACL: 'private'
    });

    // Upload to S3
    await s3Client.send(uploadCommand);
    
    // Construct the S3 URL
    const s3Url = `https://${s3Config.bucket}.s3.${s3Config.region}.amazonaws.com/${fileKey}`;
    
    
    return s3Url;
  } catch (error) {
    console.error(`❌ S3Upload: Failed to upload screenshot:`, error);
    throw error;
  }
};

/**
 * Upload a file to S3 and return the URL
 * @param {File} file - The file to upload
 * @param {string} companyId - Company ID for folder structure
 * @param {string} candidateId - Candidate ID for folder structure
 * @param {string} fileType - Type of file ('resume' or 'profileImage')
 * @returns {Promise<string>} - S3 URL of uploaded file
 */
export const uploadFileToS3 = async (file, companyId, candidateId, fileType = 'resume') => {
  try {

    // Initialize S3 if not already done
    if (!s3Client || !s3Config) {
      await initializeS3Config();
    }

    // Create folder structure: candidates/companyId/candidateId/
    const folderPath = `candidates/${companyId}/${candidateId}`;
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const fileKey = `${folderPath}/${fileName}`;


    // Convert file to ArrayBuffer
    const fileBuffer = await file.arrayBuffer();
    

    const uploadCommand = new PutObjectCommand({
      Bucket: s3Config.bucket,
      Key: fileKey,
      Body: new Uint8Array(fileBuffer),
      ContentType: file.type,
      ACL: 'private'
    });

    // Upload to S3
    await s3Client.send(uploadCommand);
    
    // Construct the S3 URL
    const s3Url = `https://${s3Config.bucket}.s3.${s3Config.region}.amazonaws.com/${fileKey}`;
    
    
    return s3Url;
  } catch (error) {
    console.error(`❌ S3Upload: Failed to upload ${fileType}:`, error);
    throw error;
  }
};

/**
 * Upload multiple files to S3
 * @param {Array} files - Array of {file, fileType} objects
 * @param {string} companyId - Company ID
 * @param {string} candidateId - Candidate ID
 * @returns {Promise<Object>} - Object with file URLs
 */
export const uploadMultipleFilesToS3 = async (files, companyId, candidateId) => {
  try {
    
    const uploadPromises = files.map(({ file, fileType }) => 
      uploadFileToS3(file, companyId, candidateId, fileType)
        .then(url => ({ [fileType]: url }))
    );

    const results = await Promise.all(uploadPromises);
    
    // Merge all results into single object
    const fileUrls = results.reduce((acc, result) => ({ ...acc, ...result }), {});
    
    
    return fileUrls;
  } catch (error) {
    console.error('❌ S3Upload: Failed to upload multiple files:', error);
    throw error;
  }
};

/**
 * Validate file before upload
 * @param {File} file - File to validate
 * @param {string} fileType - Type of file ('resume' or 'profileImage')
 * @returns {boolean} - Whether file is valid
 */
export const validateFile = (file, fileType) => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  
  if (file.size > maxSize) {
    throw new Error(`File size exceeds 10MB limit`);
  }
  
  if (fileType === 'resume') {
    if (file.type !== 'application/pdf') {
      throw new Error('Only PDF files are allowed for resume');
    }
  } else if (fileType === 'profileImage') {
    if (!file.type.startsWith('image/')) {
      throw new Error('Only image files are allowed for profile picture');
    }
  }
  
  return true;
};

export default {
  uploadFileToS3,
  uploadScreenshotToS3,
  uploadMultipleFilesToS3,
  validateFile
};